import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FiAlertTriangle,
  FiArrowDown,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
  FiMapPin,
  FiZap,
} from "react-icons/fi";
import robotUpload from "../../assets/ai-journey/robot-upload.JPG";
import robotReading from "../../assets/ai-journey/robot-reading.JPG";
import robotLocations from "../../assets/ai-journey/robot-locations.JPG";
import robotReady from "../../assets/ai-journey/robot-ready.JPG";
import "../../styles/AiImportPremiumJourney.css";

const IMAGES = [robotUpload, robotReading, robotLocations, robotReady];

const normalized = (value) => String(value || "").trim().toUpperCase();

export const getAiJourneyStage = (job) => {
  if (normalized(job?.status) === "COMPLETED") return 3;
  switch (normalized(job?.stage)) {
    case "UPLOAD_RECEIVED":
    case "UPLOADING":
      return 0;
    case "LOCATION_RESOLUTION":
    case "LOCATIONS_RESOLVING":
      return 2;
    case "PROCESSING":
    case "DOCUMENT_ANALYSIS":
    case "INTELLIGENCE_PROCESSING":
    case "RECOVERED":
    case "EXTRACTION_COMPLETED":
    case "DRAFT_READY":
      return 1;
    default:
      return 0;
  }
};

const readableSize = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "";
  return value < 1024 * 1024
    ? `${(value / 1024).toFixed(1)} KB`
    : `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const hasCoordinates = (point) => Boolean(String(point?.coordinates || "").trim());

// Coordinates and geographic verification are deliberately different concepts.
// Only claim verification when the resolver's evidence supports it.
const hasVerifiedLocation = (point) => {
  if (!hasCoordinates(point)) return false;
  if (point?.aiGeocode?.requiresReview === true ||
      point?.aiPlaceResolution?.requiresReview === true) return false;
  return normalized(point?.aiGeocode?.status) === "MATCH" ||
    normalized(point?.aiPlaceResolution?.status) === "MATCH" ||
    normalized(point?.aiGeocode?.proposedLocation?.reviewStatus) === "CONFIRMED_BY_DISPATCHER";
};

export default function AiImportProgressExperience({
  job,
  stops = [],
  onContinueToDetails,
}) {
  const { t } = useTranslation("create_load");
  const [minimized, setMinimized] = useState(false);

  const state = normalized(job?.status);
  const failed = ["FAILED", "CANCELLED", "CANCELED", "TIMED_OUT"].includes(state);
  const completed = state === "COMPLETED";
  const stage = getAiJourneyStage(job);
  const validStops = Array.isArray(stops)
    ? stops.filter(({ point }) => Boolean(
        point?.location || point?.placeName || point?.address || point?.city
      ))
    : [];
  const pickups = validStops.filter(({ type }) => type === "PICKUP").length;
  const deliveries = validStops.filter(({ type }) => type === "DELIVERY").length;
  const geoCount = validStops.filter(({ point }) => hasCoordinates(point)).length;
  const verifiedCount = validStops.filter(({ point }) => hasVerifiedLocation(point)).length;
  const requiresReview = job?.requiresReview === true ||
    (completed && validStops.length > 0 && verifiedCount < validStops.length);

  useEffect(() => {
    if (!job) return undefined;

    if (!completed || requiresReview) {
      setMinimized(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setMinimized(true);
    }, 1800);

    return () => window.clearTimeout(timer);
  }, [completed, requiresReview, job?.fileName]);

  if (!job) return null;

  const stages = [
    {
      title: stage === 0
        ? t("aiJourney.uploadActive", { defaultValue: "Receiving document" })
        : t("aiJourney.upload", { defaultValue: "Document received" }),
      detail: t("aiJourney.uploadDetail", { defaultValue: "Preparing the document for analysis" }),
    },
    {
      title: t("aiJourney.reading", { defaultValue: "Reading transport order" }),
      detail: t("aiJourney.readingDetail", { defaultValue: "Identifying stops, dates, cargo and references" }),
    },
    {
      title: t("aiJourney.locations", { defaultValue: "Verifying locations" }),
      detail: t("aiJourney.locationsDetail", { defaultValue: "Matching addresses and coordinates" }),
    },
    {
      title: t("aiJourney.review", { defaultValue: "Preparing your load" }),
      detail: t("aiJourney.reviewDetail", { defaultValue: "Making the extracted information available for review" }),
    },
  ];

  const headlines = [
    t("aiJourney.uploadTitle", { defaultValue: "Uploading your file..." }),
    t("aiJourney.readingTitle", { defaultValue: "Reading your transport order..." }),
    t("aiJourney.locationsTitle", { defaultValue: "Verifying locations..." }),
    requiresReview
      ? t("aiJourney.reviewTitle", { defaultValue: "Ready for your review" })
      : t("aiJourney.completeTitle", { defaultValue: "Your load is ready for review!" }),
  ];

  const descriptions = [
    t("aiJourney.uploadDescription", { defaultValue: "Your document is on its way to LOADEvo AI." }),
    t("aiJourney.readingDescription", { defaultValue: "Our AI is extracting the transport information." }),
    t("aiJourney.locationsDescription", { defaultValue: "The fields are editable while locations are being checked." }),
    requiresReview
      ? t("aiJourney.reviewDescription", { defaultValue: "Some locations need confirmation before creating the load." })
      : t("aiJourney.completeDescription", { defaultValue: "The extracted fields are available. Please check them before saving." }),
  ];

  const headline = failed
    ? t("aiJourney.failedTitle", { defaultValue: "We couldn't finish processing this order" })
    : headlines[stage];
  const description = failed
    ? (job?.error?.message || t("aiJourney.failedDescription", {
        defaultValue: "Please review the error and try the import again.",
      }))
    : descriptions[stage];
  const fileName = String(job.fileName || "Transport order").trim();
  const size = readableSize(job.fileSize);

  return (
    <section className={`loadevo-ai-journey${failed ? " is-failed" : ""}${completed ? " is-completed" : ""}${minimized ? " is-minimized" : ""}`}
      aria-label={t("aiJourney.sectionLabel", { defaultValue: "LOADEvo AI import progress" })}>
      <header className="loadevo-ai-journey__topbar">
        <div className="loadevo-ai-journey__brand">
          <span className="loadevo-ai-journey__brand-mark" aria-hidden="true"><FiZap /></span>

          <span className="loadevo-ai-journey__brand-full">
            LOADEvo <strong>AI Journey</strong>
          </span>

          <span className="loadevo-ai-journey__brand-short">
            LOADEvo <strong>AI</strong>
          </span>

          <span className="loadevo-ai-journey__stage-count">
            {String(stage + 1).padStart(2, "0")} / 04
          </span>
        </div>
        <button className="loadevo-ai-journey__collapse" type="button"
          aria-expanded={!minimized}
          onClick={() => setMinimized((value) => !value)}>
          {minimized
            ? t("aiJourney.expand", { defaultValue: "Expand" })
            : t("aiJourney.minimize", { defaultValue: "Minimize" })}
          {minimized ? <FiChevronDown aria-hidden="true" /> : <FiChevronUp aria-hidden="true" />}
        </button>
      </header>

      <div className="loadevo-ai-journey__headline" aria-live="polite" aria-atomic="true">
        <h3>
          {minimized && completed && (
            <FiCheck
              className="loadevo-ai-journey__ready-icon"
              aria-hidden="true"
            />
          )}

          <span>
            {minimized && completed
              ? requiresReview
                ? t("aiJourney.attentionRequired", {
                    defaultValue: "Review needed",
                  })
                : t("aiJourney.readyShort", {
                    defaultValue: "Ready for review",
                  })
              : headline}
          </span>
        </h3>

        <p>{description}</p>
      </div>

      {!minimized && (
        <div className="loadevo-ai-journey__body">
          <div className="loadevo-ai-journey__illustration">
            <img key={stage} src={IMAGES[stage]} alt="" aria-hidden="true" width="435" height="316" />
            <div className="loadevo-ai-journey__file">
              <FiFileText aria-hidden="true" />
              <div className="loadevo-ai-journey__file-copy">
                <strong title={fileName}>{fileName}</strong>
                {size && <small>{size}</small>}
              </div>
            </div>
          </div>

          <div className="loadevo-ai-journey__information">
            {failed ? (
              <div className="loadevo-ai-journey__alert" role="alert">
                <FiAlertTriangle aria-hidden="true" />
                <span>{description}</span>
              </div>
            ) : completed ? (
              <div className={`loadevo-ai-journey__result${requiresReview ? " is-review" : ""}`}>
                <div className="loadevo-ai-journey__result-header">
                  {requiresReview ? <FiAlertTriangle aria-hidden="true" /> : <FiCheck aria-hidden="true" />}
                  <strong>{requiresReview
                    ? t("aiJourney.attentionRequired", { defaultValue: "Review needed" })
                    : t("aiJourney.analysisFinished", { defaultValue: "Analysis finished" })}
                  </strong>
                </div>
                <div className="loadevo-ai-journey__result-rows">
                  {validStops.length > 0 && (
                    <>
                      <span>{t("aiJourney.pickups", { defaultValue: "Pickups" })}<strong>{pickups}</strong></span>
                      <span>{t("aiJourney.deliveries", { defaultValue: "Deliveries" })}<strong>{deliveries}</strong></span>
                      <span><FiMapPin aria-hidden="true" /> {t("aiJourney.verified", { defaultValue: "Verified locations" })}<strong>{verifiedCount} / {validStops.length}</strong></span>
                      <span>{t("aiJourney.withCoordinates", { defaultValue: "Locations with coordinates" })}<strong>{geoCount} / {validStops.length}</strong></span>
                    </>
                  )}
                  {validStops.length === 0 && <span>
                    {t("aiJourney.fieldsReady", { defaultValue: "Extracted fields are ready for manual review." })}
                  </span>}
                </div>
              </div>
            ) : (
              <div className="loadevo-ai-journey__checklist">
                {stages.map(({ title, detail }, index) => {
                  const done = index < stage;
                  const active = index === stage;
                  return (
                    <div key={title}
                      className={`loadevo-ai-journey__check${done ? " is-done" : ""}${active ? " is-active" : ""}`}>
                      <span className="loadevo-ai-journey__check-indicator" aria-hidden="true">
                        {done ? <FiCheck /> : active ? <span className="loadevo-ai-journey__active-center" /> : null}
                      </span>
                      <div className="loadevo-ai-journey__check-copy">
                        <strong>{title}</strong>
                        {active && <small>{detail}</small>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!failed && !completed && stage === 2 && (
              <p className="loadevo-ai-journey__background-hint">
                {t("aiJourney.editHint", { defaultValue: "You can edit the available fields now. Your changes won't be silently overwritten." })}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="loadevo-ai-journey__steps" aria-label={t("aiJourney.stepsLabel", { defaultValue: "Import stages" })}>
        {stages.map(({ title }, index) => (
          <div key={title}
            className={`loadevo-ai-journey__step${completed || index < stage ? " is-done" : ""}${!completed && index === stage ? " is-active" : ""}`}
            aria-current={!completed && index === stage ? "step" : undefined}>
            <span className="loadevo-ai-journey__step-point" aria-hidden="true">
              {completed || index < stage ? <FiCheck /> : index + 1}
            </span>
            <span>{title}</span>
          </div>
        ))}
      </div>

      {completed && typeof onContinueToDetails === "function" && (
        <div className="loadevo-ai-journey__continue">
          <button
            type="button"
            className="loadevo-ai-journey__continue-button"
            onClick={onContinueToDetails}
            aria-label={t("aiJourney.reviewDetails", {
              defaultValue: "Review load details",
            })}
            title={t("aiJourney.reviewDetails", {
              defaultValue: "Review load details",
            })}
          >
            <FiArrowDown aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
}
